<script setup lang="ts">
import { onBeforeMount } from 'vue';
import { RenderPhone, RenderAddress } from '@hfc/ui-core';
import { useUrlHelper } from '@/page/infrastructure/composition/useUrlHelper';
import BreadcrumbGenerator from '@/page/infrastructure/components/BreadcrumbGenerator.vue';
import ServicesSection from '@/page/infrastructure/components/ServicesSection.ce.vue';
import StaffMember from '@/shared/infrastructure/components/staffMember/StaffMember.vue';
import { useStaffMemberStore } from '@/shared/infrastructure/composition/useStaffMemberStore';
import '@justinribeiro/lite-youtube';
import LicenseContent from '@/page/infrastructure/components/LicenseContent.vue';

const { getCdnUrl, getBaseUrl } = useUrlHelper();

const { staffMemberStore, staffMemberState } = useStaffMemberStore();

const breadcrumbs = [
  { label: 'California Health Insurance', href: '/' },
  { label: 'About Us', href: '/about-us' }
];

onBeforeMount(async () => {
  await staffMemberStore.getStaffMembers();
});
</script>

<template>
  <!-- Header -->
  <div class="w-full relative flex items-center justify-center bg-white">
    <div :style="`background-image: url('${getCdnUrl('/assets/img/aboutUs/about_us_header_bg.webp')}');`" class="relative w-full h-[200px] object-cover before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-full before:bg-center before:bg-gradient-to-br before:from-blue-900 before:to-blue-500 before:opacity-90" />
    <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center">
      <h1 class="text-white text-[56px] font-bold mb-2 min-w-[300px] text-center">About Us</h1>
      <BreadcrumbGenerator :breadcrumbs="breadcrumbs" class="text-white hidden md:flex" link-class="text-white underline text-base" />
      <a :href="getBaseUrl('/')" class="mt-4 inline-block md:hidden cursor-pointer text-white text-base">&lt; <span class="underline">Back to California Health Insurance</span></a>
    </div>
  </div>
  <!-- End Header -->

  <!-- Section: Get to Know Us -->
  <section class="w-full flex flex-col items-center justify-center bg-white pt-14 md:pt-24 px-5">
    <div class="w-full max-w-6xl mx-auto text-center">
      <h2 class="text-[42px] font-medium text-blue-600 mb-6">Get to Know Us</h2>
      <p class="text-center text-[17px]">Finding the right health insurance can be stressful and time-consuming. At Health for California, we are dedicated to making the application process fast, feasible, and free. Learn more about our leadership team below.</p>
    </div>
    <div class="flex w-full flex-col md:flex-row md:flex-wrap justify-center gap-x-6 gap-y-10 md:gap-y-16 max-w-6xl mx-auto py-16">
      <div v-for="person in staffMemberState.staffMembers" :key="person.id" class="w-full md:w-[340px]">
        <StaffMember :person="person" enable-modal compact-mode />
      </div>
    </div>
  </section>
  <!-- End Section: Get to Know Us -->

  <!-- Section: HFC Insurance Center  -->
  <section class="w-full bg-white pt-6 grid grid-cols-1 md:grid-cols-2">
    <img :src="getCdnUrl('/assets/img/aboutUs/hfc_center.webp')" alt="HFC Center" class="w-full object-cover h-auto md:h-[560px]" />
    <div class="flex flex-col items-start justify-center bg-gray-100 py-3 my-0 md:my-3 lg:my-12">
      <div class="h-full flex flex-col items-start justify-center md:max-w-[600px] p-5 md:px-8 lg:pl-12">
        <h2 class="text-[32px] md:text-[42px] text font-medium text-blue-600 mb-6">Health for California Insurance Center</h2>
        <p class="text-gray-800 text-[17px]">We are a health insurance agency that's been operating in California since 2004. On the web, we are located at <a :href="getBaseUrl('/')" class="text-blue-600">www.HealthForCalifornia.com</a>. All of our agents are licensed by the <a href="https://www.insurance.ca.gov/" class="text-blue-600" target="_blank">California Department of Insurance</a> as well as by <a href="https://coveredca.com/" target="_blank" class="text-blue-600">Covered California</a>, the California health insurance exchange. Our agents are guaranteed to know their stuff, serve with respect and kindness, and make purchasing health insurance as easy as possible for you in the new world of Healthcare Reform.</p>
      </div>
    </div>
  </section>
  <!-- End Section: HFC Insurance Center  -->

  <!-- Section: How We Can Help  -->
  <section class="w-full bg-white pt-14 md:pt-24 px-5">
    <div class="w-full grid grid-cols-1 lg:grid-cols-1 max-w-6xl mx-auto">
      <div class="flex flex-col items-start justify-center p-5 md:py-8 md:px-8 lg:pr-12">
        <h2 class="text-[32px] md:text-[42px] font-medium text-blue-600 my-6 md:mt-0">How We Can Help</h2>
        <p class="text-[17px] mb-3">We make it simple. We'll get you a quick quote, help you discover the plan that's right for you, and introduce you to the application process. We can also help you calculate your government subsidy, see if you qualify for cost sharing and help you enroll in a health plan that will meet your needs, fit your budget and keep you from getting a penalty.</p>
        <p class="text-[17px]">We use simple everyday language to help you understand the plans and the process.</p>
      </div>
    </div>
  </section>
  <!-- End Section: How We Can Help  -->

  <!-- Section: Why We Do It  -->
  <section class="w-full bg-white pt-14 md:pt-24 px-5">
    <div class="w-full max-w-6xl mx-auto text-center">
      <h2 class="text-[32px] md:text-[42px] font-medium text-blue-600 mb-6">Why We Do It</h2>
      <p class="text-center text-[17px mb-3">Aside from healthcare being our livelihood, we like people and want to help you succeed in obtaining coverage. Instead of dreading shopping for insurance, feeling confused or being pressured to buy something, we want you to be able to get the help you are looking for. Like you, we are all consumers who need to shop for a variety of products and services. As to how we get paid, when you enroll through us (at the same prices you’ll get anywhere), we get a commission from the health insurance carrier you choose. You do not pay extra by enrolling with us.</p>
      <p class="text-center text-[17px]">Looking forward to serving you soon!</p>
    </div>
  </section>
  <!-- End Section: Why We Do It  -->

  <!-- Section: Contact Us  -->
  <section class="w-full bg-white pt-14 px-5">
    <div class="grid grid-cols-1 lg:grid-cols-[450px_1fr] max-w-6xl mx-auto rounded-3xl bg-blue-100 p-7 lg:p-12 items-center">
      <div class="pb-6 border-b lg:border-b-0 lg:border-r lg:pb-0 lg:pr-12 border-gray-300">
        <h2 class="text-[32px] font-medium text-blue-600 mb-6">Contact Us</h2>
        <p class="text-[17px]">We are eager to hear about your coverage needs as well as your customer experience. Your comments, questions or suggestions are valuable feedback that keeps us on our toes. After all, we are here to serve. Please
          <feedback-modal label="leave us your feedback" display-mode="link">
            <span class="text-blue-600 cursor-pointer">leave us your feedback</span>
          </feedback-modal> or see our <a :href="getBaseUrl('/reviews')" class="text-blue-600">customer reviews.</a></p>
      </div>
      <div class="lg:pl-12 grid grid-cols-1 lg:grid-cols-2 gap-x-6 w-full">
        <div class="flex flex-col gap-3 mt-6 lg:mt-0">
          <div><RenderPhone icon-color="text-yellow-500" label="Live Support" phone="17075717590" /></div>
          <div><RenderPhone icon-color="text-yellow-500" label="Toll-Free" phone="18777524737" /></div>
          <div><RenderPhone icon-color="text-yellow-500" label="Fax" phone="18664399993" is-fax /></div>
        </div>
        <div class="flex flex-col gap-3">
          <div><RenderAddress two-lines icon-color="text-yellow-500" line-one="4000 Montgomery Dr Ste D7" city="Santa Rosa" state="CA" zip="95405" /></div>
          <a href="https://www.google.com/maps/place/Health+For+California+-+Covered+CA+Enrollment/@38.4527366,-122.6758353,16z/data=!3m1!4b1!4m6!3m5!1s0x808447f81dcf29bb:0xb47bb6499a55e777!8m2!3d38.4527366!4d-122.6732604!16s%2Fg%2F1q66qmhwb?entry=ttu&g_ep=EgoyMDI1MTExMC4wIKXMDSoASAFQAw%3D%3D" target="blank" class="text-blue-600 pl-5">
            <strong>Get Directions</strong>
            <hfc-icon icon="arrow-right" class="text-blue-600 ml-2" />
          </a>
        </div>
      </div>
    </div>
  </section>
  <!-- End Section: Contact Us  -->

  <!-- Section: License  -->
  <section class="w-full bg-white">
    <div class="flex flex-col max-w-7xl mx-auto px-3 pt-8">
      <LicenseContent></LicenseContent>
    </div>
  </section>
  <!-- End Section: License  -->
   <!-- Section: Service -->
    <section class="w-full bg-white">
      <div class="flex flex-col max-w-7xl mx-auto px-3 pb-8">
        <ServicesSection />
      </div>
    </section>
  <!--End Section: Service -->

</template>
